import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios'; // Axios puro (sem interceptors)
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, Plus, Trash2, MapPin, Loader2, AlertCircle } from 'lucide-react';
import defaultLogo from '@/assets/logo.png'; 

// --- CONFIGURAÇÃO DA API (CRÍTICO) ---
// Tenta pegar do .env, se não tiver, usa o localhost padrão
// ATENÇÃO: Se você usa Vite, é import.meta.env.VITE_API_URL
const BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace('/api', '') 
  : "http://127.0.0.1:8000";

const API_URL = `${BASE_URL}/api`;

// --- FUNÇÕES AUXILIARES ---
const validarCPF = (cpf) => {
  cpf = cpf.replace(/[^\d]+/g, '');
  if (cpf === '') return false;
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  let add = 0;
  for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
  let rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9))) return false;
  add = 0;
  for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(10))) return false;
  return true;
};

const formatarCPF = (value) => {
  return value
    .replace(/\D/g, '') 
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1'); 
};

const formatarDataBR = (value) => {
  value = value.replace(/\D/g, ''); 
  if (value.length > 2 && value.length <= 4) {
    value = value.replace(/(\d{2})(\d+)/, '$1/$2');
  } else if (value.length > 4) {
    value = value.replace(/(\d{2})(\d{2})(\d+)/, '$1/$2/$3');
  }
  return value;
};

const validarDataReal = (dataString) => {
  if (!dataString || dataString.length !== 10) return false;
  const partes = dataString.split('/');
  if (partes.length !== 3) return false;
  const dia = parseInt(partes[0], 10);
  const mes = parseInt(partes[1], 10);
  const ano = parseInt(partes[2], 10);
  if (ano < 1900 || ano > new Date().getFullYear() + 1) return false;
  if (mes < 1 || mes > 12) return false;
  const diasNoMes = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if ((ano % 400 === 0) || (ano % 4 === 0 && ano % 100 !== 0)) { diasNoMes[2] = 29; }
  if (dia <= 0 || dia > diasNoMes[mes]) return false;
  return true;
};

const converterDataParaBackend = (dataBR) => {
  if (!dataBR || dataBR.trim() === '') return null;
  if (!validarDataReal(dataBR)) return null;
  const [dia, mes, ano] = dataBR.split('/');
  return `${ano}-${mes}-${dia}`;
};

const initialForm = {
  nome: '',
  email: '',
  telefone: '',
  cpf: '',
  data_nascimento: '',
};

export default function CadastroClientePublico() {
  const { id } = useParams();
  
  // Limpeza extra para garantir que pegamos só o ID
  const agencia_id = id ? id.trim() : null;

  const [agencia, setAgencia] = useState(null);
  const [loadingAgencia, setLoadingAgencia] = useState(true);
  const [errorAgencia, setErrorAgencia] = useState(false);

  const [passageiros, setPassageiros] = useState([{ ...initialForm, id: 0 }]);
  const [nextId, setNextId] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  
  const bottomRef = useRef(null);

  // --- BUSCA DADOS DA AGÊNCIA ---
  useEffect(() => {
    const fetchAgencia = async () => {
        if (!agencia_id) {
            console.error("ID da agência não encontrado na URL");
            setLoadingAgencia(false);
            return;
        }

        try {
            // URL Absoluta para garantir
            const url = `${API_URL}/clientes/publico/agencia/${agencia_id}`;
            console.log("Tentando buscar agência em:", url);

            // Usando axios puro para evitar headers de Auth
            const response = await axios.get(url);
            
            setAgencia(response.data);
            document.title = `Cadastro - ${response.data.nome}`;
        } catch (error) {
            console.error("Erro ao buscar agência:", error);
            setErrorAgencia(true);
        } finally {
            setLoadingAgencia(false);
        }
    };
    fetchAgencia();
    
    return () => { document.title = "Operya ERP"; };
  }, [agencia_id]);

  const handleChange = (id, e) => {
    let { name, value } = e.target;
    if (name === 'cpf') value = formatarCPF(value);
    if (name === 'data_nascimento') value = formatarDataBR(value);
    setPassageiros(prev => prev.map(p => p.id === id ? { ...p, [name]: value } : p));
  };

  const addPassageiro = () => {
    setPassageiros(prev => [...prev, { ...initialForm, id: nextId }]);
    setNextId(prev => prev + 1);
    setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  const removePassageiro = (id) => {
    if (passageiros.length <= 1) { toast.info("É necessário cadastrar pelo menos um passageiro."); return; }
    setPassageiros(prev => prev.filter(p => p.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!lgpdAccepted) {
      toast.error("Você precisa aceitar os termos de uso.");
      setLoading(false);
      return;
    }
    
    // Validações
    for (const [index, passageiro] of passageiros.entries()) {
      const num = index + 1;
      if (!passageiro.nome.trim()) { toast.error(`Passageiro ${num}: Nome é obrigatório.`); setLoading(false); return; }
      if (!validarCPF(passageiro.cpf)) { toast.error(`Passageiro ${num}: CPF inválido.`); setLoading(false); return; }
      if (passageiro.data_nascimento) {
        if (!validarDataReal(passageiro.data_nascimento)) { toast.error(`Passageiro ${num}: Data inválida.`); setLoading(false); return; }
      }
    }

    try {
      const promises = passageiros.map(async (passageiro) => {
        const payload = {
          nome: passageiro.nome.trim(),
          email: passageiro.email.trim() || null,
          telefone: passageiro.telefone.trim() || null,
          cpf: passageiro.cpf.replace(/\D/g, ''),
          data_nascimento: converterDataParaBackend(passageiro.data_nascimento),
        };
        // URL Absoluta
        await axios.post(`${API_URL}/clientes/publico/${agencia_id}`, payload);
      });
      
      await Promise.all(promises);
      setSuccess(true);
      toast.success("Cadastro realizado com sucesso!");
      window.scrollTo(0, 0);

    } catch (error) {
      console.error(error);
      toast.error('Erro ao enviar cadastro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingAgencia) {
      return (
          <div className="flex h-screen items-center justify-center bg-gray-50">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
          </div>
      );
  }

  // Sucesso
  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 animate-in fade-in zoom-in duration-500">
        <div className="w-full max-w-lg p-8 bg-white rounded-xl shadow-lg text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Cadastro Recebido!</h1>
          <p className="text-gray-600 mb-6">Seus dados foram enviados para <strong>{agencia?.nome}</strong>.</p>
          <p className="text-sm text-gray-400">Pode fechar esta página.</p>
        </div>
      </div>
    );
  }

  // Erro 404 / Link Inválido
  if (errorAgencia || !agencia) {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50 p-4">
            <div className="text-center max-w-md">
                <div className="bg-red-50 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">Link Inválido ou Expirado</h2>
                <p className="text-gray-600 mt-2">
                    Não foi possível carregar os dados da agência. <br/>
                    <span className="text-xs text-gray-400 mt-2 block break-all">ID: {agencia_id || 'Não fornecido'}</span>
                </p>
                <div className="mt-4 text-xs text-gray-400">
                   Verifique a URL da API: {API_URL}
                </div>
                <Button variant="outline" className="mt-6" onClick={() => window.location.reload()}>Tentar Novamente</Button>
            </div>
        </div>
      );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 sm:p-6">
      <div className="w-full max-w-lg p-6 sm:p-8 space-y-6 bg-white rounded-xl shadow-xl border border-gray-100">
        
        {/* CABEÇALHO */}
        <div className="text-center border-b border-gray-100 pb-6">
          <div className="inline-flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full mb-4 bg-white p-2 overflow-hidden shadow-sm border border-gray-100">
            <img 
                src={agencia.logo_url || defaultLogo} 
                alt="Logo" 
                className="w-full h-full object-contain"
                onError={(e) => { e.target.src = defaultLogo; }}
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{agencia.nome}</h1>
          <div className="mt-3 inline-flex items-center gap-2 text-sm text-blue-700 bg-blue-50 py-1.5 px-4 rounded-full">
             <MapPin className="w-3.5 h-3.5" />
             <span className="truncate max-w-[250px]">Enviando para <strong>{agencia.nome}</strong></span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="text-sm text-gray-500 text-center">Preencha os dados dos passageiros.</div>

          {passageiros.map((passageiro, index) => (
            <div key={passageiro.id} className="p-5 border border-gray-200 rounded-xl space-y-4 bg-gray-50/50 shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <span className="bg-blue-600 text-white w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold">{index + 1}</span>
                    Passageiro
                </h3>
                {passageiros.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" className="text-red-500 h-8 px-2" onClick={() => removePassageiro(passageiro.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div>
                <Label htmlFor={`nome-${passageiro.id}`}>Nome Completo</Label>
                <Input 
                  id={`nome-${passageiro.id}`} name="nome" value={passageiro.nome} 
                  onChange={(e) => handleChange(passageiro.id, e)} required className="bg-white mt-1" 
                />
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor={`email-${passageiro.id}`}>Email</Label>
                  <Input 
                    id={`email-${passageiro.id}`} name="email" type="email" value={passageiro.email} 
                    onChange={(e) => handleChange(passageiro.id, e)} className="bg-white mt-1" 
                  />
                </div>
                <div>
                  <Label htmlFor={`telefone-${passageiro.id}`}>Celular</Label>
                  <Input 
                    id={`telefone-${passageiro.id}`} name="telefone" inputMode="tel" value={passageiro.telefone} 
                    onChange={(e) => handleChange(passageiro.id, e)} className="bg-white mt-1" 
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`cpf-${passageiro.id}`}>CPF</Label>
                  <Input 
                    id={`cpf-${passageiro.id}`} name="cpf" value={passageiro.cpf} 
                    onChange={(e) => handleChange(passageiro.id, e)} maxLength={14} inputMode="numeric" required className="bg-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor={`data_nascimento-${passageiro.id}`}>Nascimento</Label>
                  <Input
                    id={`data_nascimento-${passageiro.id}`} name="data_nascimento" type="text" placeholder="DD/MM/AAAA"
                    inputMode="numeric" maxLength={10} value={passageiro.data_nascimento} 
                    onChange={(e) => handleChange(passageiro.id, e)} className="bg-white mt-1"
                  />
                </div>
              </div>
            </div>
          ))}

          <div ref={bottomRef}></div>

          <Button type="button" variant="outline" className="w-full border-dashed border-2 py-6 text-gray-600 hover:text-blue-600" onClick={addPassageiro}>
            <Plus className="w-5 h-5 mr-2" /> Adicionar Outro Passageiro
          </Button>

          <div className="pt-2 flex items-start space-x-3 p-4 bg-blue-50/80 rounded-lg border border-blue-100">
              <Checkbox id="lgpd" checked={lgpdAccepted} onCheckedChange={setLgpdAccepted} className="mt-1 border-blue-400 data-[state=checked]:bg-blue-600" />
              <div className="grid gap-1.5 leading-none">
                <label htmlFor="lgpd" className="text-sm font-medium leading-none cursor-pointer text-blue-900">Consentimento de Dados</label>
                <p className="text-xs text-blue-800/70">Autorizo o envio dos dados para emissão de passagens.</p>
              </div>
          </div>
          
          <Button type="submit" disabled={loading || !lgpdAccepted} className="w-full text-lg h-14 font-bold rounded-xl" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirmar Cadastro'}
          </Button>
        </form>
      </div>
    </div>
  );
}